/**
 * useTrend — the A2 metric-trend URL surface (CD-8), a companion to useScope kept
 * separate so useScope's drill grammar is untouched. `trend_metric=<registry id>`
 * opens the trend chart section. Both hooks read/write the same URL and preserve
 * each other's params (URLSearchParams copy), so a trend survives site/cadence/drill
 * changes and vice-versa. A malformed id parses to no-trend, never a crash.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

const TREND_PARAM = 'trend_metric'
const METRIC_ID_RE = /^[a-z][a-z0-9_]*$/ // registry ids are lower_snake tokens (CD-4)

export interface Trend {
  trend: string | null
  openTrend: (metricId: string) => void
  closeTrend: () => void
}

export function useTrend(): Trend {
  const [searchParams, setSearchParams] = useSearchParams()
  const raw = searchParams.get(TREND_PARAM)
  const trend = useMemo(() => (raw && METRIC_ID_RE.test(raw) ? raw : null), [raw])
  const openTrend = useCallback(
    (metricId: string) =>
      setSearchParams((previous) => {
        const params = new URLSearchParams(previous)
        params.set(TREND_PARAM, metricId)
        return params
      }),
    [setSearchParams]
  )
  const closeTrend = useCallback(
    () =>
      setSearchParams((previous) => {
        const params = new URLSearchParams(previous)
        params.delete(TREND_PARAM)
        return params
      }),
    [setSearchParams]
  )
  return { trend, openTrend, closeTrend }
}
